import { isDate } from 'date-fns';
import i18n, { type Resource } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import { TimestampFormat, formatTimestamp } from './helpers/datetime';

const FALLBACK_LANGUAGE = 'en-CA';
const translationModules = import.meta.glob<string>('./translations/**/*.json', {
    query: '?raw',
    import: 'default',
});
const languageResourcesCache = new Map<string, Promise<Resource[string]>>();

const getPathSegments = (filePath: string) =>
    filePath
        .replace(/^\.\//, '')
        .split('/')
        .map((segment) => segment.replace(/\.json$/, ''));

const getLanguageFromPath = (filePath: string) => getPathSegments(filePath)[1];

const getNamespaceFromPath = (filePath: string) => {
    const segments = getPathSegments(filePath);
    return segments.length === 2 ? 'translation' : segments.slice(2).join('.');
};

async function loadLanguageResources(language: string): Promise<Resource[string]> {
    const cachedResources = languageResourcesCache.get(language);
    if (cachedResources) {
        return cachedResources;
    }

    const pendingResources = (async () => {
        const matchingModulePaths = Object.keys(translationModules).filter(
            (filePath) => getLanguageFromPath(filePath) === language,
        );
        const resourcesForLanguage: NonNullable<Resource[string]> = {};

        await Promise.all(
            matchingModulePaths.map(async (filePath) => {
                const namespace = getNamespaceFromPath(filePath);
                resourcesForLanguage[namespace] = JSON.parse(await translationModules[filePath]());
            }),
        );

        return resourcesForLanguage;
    })();

    languageResourcesCache.set(language, pendingResources);
    return pendingResources;
}

const hasLoadedLanguage = (language: string) =>
    Object.keys(i18n.store.data?.[language] ?? {}).length > 0;

export const ensureI18NextLanguage = async (language: string): Promise<void> => {
    if (!language || hasLoadedLanguage(language)) {
        return;
    }

    const resourcesForLanguage = await loadLanguageResources(language);
    for (const [namespace, resource] of Object.entries(resourcesForLanguage)) {
        i18n.addResourceBundle(language, namespace, resource, true, true);
    }
};

export const configureI18Next = async (): Promise<void> => {
    const fallbackResources = await loadLanguageResources(FALLBACK_LANGUAGE);
    // i18n is being added here only as a proof-of-concept.
    // TODO:
    // 1. Figure out a way to namespace the keys (e.g. "facilities.details.facilityDetails.provinceLabel").
    // 2. Add en-US translations where appropriate.
    await i18n
        .use(LanguageDetector)
        .use(initReactI18next)
        .init({
            // Our translation resources use i18next v4 plural keys (`*_one`, `*_other`), so we must
            // use v4 JSON compatibility. v3 mode ignores those keys.
            compatibilityJSON: 'v4',
            resources: {
                [FALLBACK_LANGUAGE]: fallbackResources,
            } as Resource,
            fallbackLng: FALLBACK_LANGUAGE,
            partialBundledLanguages: true,
            interpolation: {
                escapeValue: false,
                format: (value, _format) => {
                    if (isDate(value)) {
                        const idx = Object.keys(TimestampFormat).indexOf(_format || '');
                        const f = Object.values(TimestampFormat)[idx] ?? TimestampFormat.DATE;
                        return formatTimestamp(value, f);
                    }
                    return value;
                },
            },
            nsSeparator: false,
        });
};
